'use client';

import { useState, useRef } from 'react';
import type { ReactElement } from 'react';
import { Fragment } from 'react';
import {
  Controller,
  type UseFormReturn,
  type FieldPath
} from 'react-hook-form';

import { Checkbox } from '@/components/ui/checkbox';
import { FormField } from './FormField';
import { PaidByEvidenceField } from './PaidByEvidenceField';
import type { InventoryItem } from '@/lib/services/inventory-items/types';
import type { Contact } from '@/lib/services/contacts/types';

interface PaidByFieldProps {
  form: UseFormReturn<Partial<InventoryItem>>;
  mode: 'read' | 'edit';
  contacts?: Contact[];
  loadingContacts?: boolean;
  clickToCopy?: boolean;
  /**
   * AI Matter Creation mode — contacts don't yet have DB IDs; their `id`
   * is a synthetic 0-based array index. The field writes to
   * `itemInfo.paidByContactRef = { contactIndex, name }` instead of
   * `payeeContactId`. `ApplyChangesService.resolvePaidByContactRef` on the
   * BE swaps the ref for a real `payeeContactId` after contacts persist.
   */
  isCreateMode?: boolean;
  /**
   * The matter, so the receipt can be uploaded against it or picked from its documents.
   * Absent in AI-creation mode, where no persisted file can be referenced yet.
   */
  matterId?: number;
}

interface PaidByContactRef {
  contactIndex: number;
  name?: string;
}

function getContactLabel(c: Contact): string | null {
  const personName = [c.firstName, c.lastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');
  return personName || c.organisationName?.trim() || null;
}

/**
 * Shared "Payee for Distribution" field for all liability forms.
 *
 * In normal matter editing, the field writes the real DB FK `payeeContactId`.
 * In AI Matter Creation (`isCreateMode`), contacts haven't been persisted yet,
 * so the field writes a temporary `itemInfo.paidByContactRef = { contactIndex, name }`
 * that the BE resolves to a real `payeeContactId` at apply time.
 *
 * In read mode, shows "Payee for Distribution: {name}" when a contact is linked.
 * In edit mode, shows a checkbox that reveals a "Payee for Distribution" dropdown.
 */
export function PaidByField({
  form,
  mode,
  contacts = [],
  loadingContacts = false,
  clickToCopy = false,
  isCreateMode = false,
  matterId
}: PaidByFieldProps): ReactElement | null {
  const item = form.getValues();
  const itemInfo = (item.itemInfo ?? {}) as Record<string, unknown>;
  const initialRef = isCreateMode
    ? (itemInfo.paidByContactRef as PaidByContactRef | null | undefined)
    : null;
  // Seed from the STORED answer, falling back to "a payee is set" only when the
  // flag is absent (an optimistic object that has not round-tripped yet).
  //
  // Known limitation, pre-existing and deliberately not widened here: unticking
  // clears the payee, so this control cannot express "a payee is set AND they are
  // the creditor" — the direct-contact-payee case. Every row reachable through it
  // is therefore an out-of-pocket one, which is what the label has always claimed.
  // Expressing the other case needs a three-way control (Estate · paid out of
  // pocket · is the creditor), which is its own change.
  const hasInitialValue = isCreateMode
    ? initialRef?.contactIndex !== undefined && initialRef.contactIndex >= 0
    : (item.paidOutOfPocket ?? !!item.payeeContactId);

  const [paidOutOfPocket, setPaidOutOfPocket] = useState(() => hasInitialValue);
  // Ref mirrors checkbox state synchronously so field validators always
  // read the latest value (avoids React batching stale-closure issues).
  const paidOutOfPocketRef = useRef(hasInitialValue);

  if (mode === 'read') {
    // Resolve contact name from the embedded object first, then fall back to
    // the contacts list (handles optimistic updates where payeeContactId is set
    // but the full contact object hasn't been fetched yet).
    let contactName: string | null = null;

    if (isCreateMode && initialRef) {
      const match = contacts.find((c) => c.id === initialRef.contactIndex);
      if (match) {
        contactName = getContactLabel(match);
      } else if (initialRef.name) {
        contactName = initialRef.name;
      }
    } else if (item.payeeContact) {
      contactName =
        [item.payeeContact.firstName, item.payeeContact.lastName]
          .filter(Boolean)
          .join(' ') || 'Contact';
    } else if (item.payeeContactId) {
      const match = contacts.find((c) => c.id === item.payeeContactId);
      if (match) {
        contactName = getContactLabel(match);
      }
    }

    if (!contactName) return null;

    return (
      <Fragment>
        <FormField
          label="Payee for Distribution"
          value={contactName}
          mode="read"
          clickToCopy={clickToCopy}
        />
        {/* Both render only when recorded. The estate account states this
            obligation before any round reimburses it, and a reader asking
            "when did they pay, and what proves it" should not have to open
            edit mode to find out. */}
        {!!item.paidByOn && (
          <FormField
            label="Date paid"
            value={item.paidByOn}
            mode="read"
            clickToCopy={clickToCopy}
          />
        )}
        {!!item.paidByEvidenceFileId && (
          <FormField label="Receipt" value="Attached" mode="read" />
        )}
      </Fragment>
    );
  }

  // Drop unnamed contacts from the dropdown. In AI Matter Creation the LLM
  // sometimes emits placeholder contacts with no firstName/lastName/orgName
  // (typically to anchor an entitlement / paidBy index reference it couldn't
  // resolve to a real name). Offering them as selectable payees produces the
  // unactionable "Contact 4" / "Contact 5" rows the lawyer can't disambiguate
  // — the row stays visible in ContactsSection where it can be edited or
  // deleted, but it doesn't belong in a picker.
  const namedContacts = contacts.flatMap((c) => {
    const label = getContactLabel(c);
    return label ? [{ contact: c, label }] : [];
  });
  if (!loadingContacts && namedContacts.length === 0) return null;

  const contactOptions = namedContacts.map(({ contact, label }) => ({
    value: String(contact.id),
    label
  }));

  // In create mode the field value is the ref object; we bind onto
  // `itemInfo.paidByContactRef` and treat the contactIndex as the selection.
  // In edit mode the field is the scalar `payeeContactId`.
  const fieldName = (
    isCreateMode ? 'itemInfo.paidByContactRef' : 'payeeContactId'
  ) as FieldPath<Partial<InventoryItem>>;

  const isValueSelected = (value: unknown): boolean => {
    if (isCreateMode) {
      const ref = value as PaidByContactRef | null | undefined;
      return ref?.contactIndex !== undefined && ref.contactIndex >= 0;
    }
    return value !== null && value !== undefined && value !== 0;
  };

  return (
    <Controller
      control={form.control}
      name={fieldName}
      rules={{
        validate: (value) => {
          if (paidOutOfPocketRef.current && !isValueSelected(value)) {
            return 'Please select a payee for distribution';
          }
          return true;
        }
      }}
      render={({ field, fieldState }) => {
        const selectedValue = isCreateMode
          ? (field.value as PaidByContactRef | null | undefined)?.contactIndex
          : (field.value as number | null | undefined);
        const selectedString =
          selectedValue !== undefined && selectedValue !== null
            ? String(selectedValue)
            : '';

        return (
          <Fragment>
            <div className="flex items-center gap-2 pt-1">
              <Checkbox
                id="paid-out-of-pocket"
                checked={paidOutOfPocket}
                onCheckedChange={(checked) => {
                  const isChecked = checked === true;
                  paidOutOfPocketRef.current = isChecked;
                  setPaidOutOfPocket(isChecked);
                  // STORE the answer, don't just hold it in component state.
                  // This checkbox says "Paid out of pocket", and until now the
                  // answer lived only here — so the planner had to guess the same
                  // fact from whether an institution was linked, and got it wrong
                  // whenever the creditor was not one. `payeeContactId` alone
                  // cannot carry it: it means BOTH "X paid this for the estate"
                  // and "X is the creditor". Not written in create mode, where
                  // the item does not exist yet.
                  if (!isCreateMode) {
                    form.setValue('paidOutOfPocket', isChecked, {
                      shouldDirty: true
                    });
                  }
                  // Unchecking clears the selection; checking keeps it as-is
                  // so the validator can flag a missing payee on submit.
                  if (!isChecked) {
                    field.onChange(null);
                  } else {
                    field.onChange(field.value);
                  }
                }}
              />
              <label
                htmlFor="paid-out-of-pocket"
                className="cursor-pointer text-sm font-medium"
              >
                Paid out of pocket
              </label>
            </div>

            {paidOutOfPocket && (
              <div>
                <FormField
                  label="Payee for Distribution"
                  value={selectedString}
                  mode="edit"
                  type="select"
                  options={contactOptions}
                  onChange={(val) => {
                    const parsed = parseInt(String(val), 10);
                    if (Number.isNaN(parsed)) {
                      field.onChange(null);
                      return;
                    }
                    if (isCreateMode) {
                      const match = contacts.find((c) => c.id === parsed);
                      field.onChange({
                        contactIndex: parsed,
                        name: (match && getContactLabel(match)) || undefined
                      });
                    } else {
                      field.onChange(parsed);
                    }
                  }}
                  loading={loadingContacts}
                  placeholder="Select contact..."
                  error={fieldState.error?.message}
                  required
                />
                <p className="text-muted-foreground mt-1 text-xs">
                  Bank details persist on the contact across distributions.
                </p>

                {/* WHEN they paid, and the receipt proving it. Both are facts about
                    the out-of-pocket payment itself, so they belong beside the payee
                    rather than anywhere else on the item.

                    Neither is available in create mode: the AI path writes a
                    `paidByContactRef` the backend resolves later, and there is no
                    persisted item yet to hang a file on. */}
                {!isCreateMode && (
                  <div className="mt-3 space-y-3">
                    <Controller
                      control={form.control}
                      name={'paidByOn' as FieldPath<Partial<InventoryItem>>}
                      render={({ field: paidOnField }) => (
                        <FormField
                          label="Date paid"
                          value={(paidOnField.value as string) || ''}
                          mode="edit"
                          type="date"
                          onChange={(val) =>
                            paidOnField.onChange(val ? String(val) : null)
                          }
                          placeholder="When they paid it"
                        />
                      )}
                    />
                    <Controller
                      control={form.control}
                      name={
                        'paidByEvidenceFileId' as FieldPath<
                          Partial<InventoryItem>
                        >
                      }
                      render={({ field: evidenceField }) => (
                        <PaidByEvidenceField
                          matterId={matterId}
                          value={evidenceField.value as number | null}
                          onChange={evidenceField.onChange}
                        />
                      )}
                    />
                  </div>
                )}
              </div>
            )}
          </Fragment>
        );
      }}
    />
  );
}
